import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Tab,
  Tabs,
} from '@mui/material';
import { DateTime } from 'luxon';
import isEqual from 'lodash/isEqual';
import { useForm, type FieldPath } from 'react-hook-form';

import { TransportationAttribute } from '@/data/models/transportation-attribute.model';
import type {
  ReservationValidations,
  TripLeg,
  TripLegType,
} from '@/data/models/reservation.model';
import type { ServicePlan } from '@/data/models/service-plan.model';
import type { Passenger } from '@/data/models/passenger.model';

import {
  buildEmptyReservationFormValues,
  buildReservationFormValuesFromTripLeg,
  MEMBER_FORM_PHONE_FIELDS,
  ReservationFormValues,
  ReservationFormInput,
} from './schema/reservationForm.schema';

import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { ReservationFormDebug } from './hooks/useDebugReservationMachines';

import { usePortalContext } from '@/contexts/PortalProvider';

import * as lookupSlice from '@/data/lookup.slice';
import * as uiSlice from '@/data/ui.slice';
import * as tripLegSlice from '@/data/tripleg.slice';
import * as accountingSlice from '@/data/accounting.slice';
import * as reservationSlice from '@/data/reservation.slice';
import { fetchPassengerPreferencesAsync } from '@/data/passenger.slice';

import reservationUtils from '@/services/reservationUtils';
import {
  ReservationFormMachinesProvider,
  useReservationFormMachines,
} from './ReservationFormMachinesContext';
import { getGurneyDataFromReservationFormSnapshot } from './machines/reservationFormMain.machine';
import {
  clearTripLegScoreCache,
  scoreAndMergeConfidenceIntoTripLegs,
} from './machines/runBundledValidation';
import { useTripLegValidationFormApplier } from './hooks/useTripLegValidationFormApplier';
import { useReservationFormResolver } from './hooks/useReservationFormResolver';
import { sendReservationFieldEdited } from './hooks/useReservationFieldEditValidation';
import { useFeatureAddressValidationForCurrentPortal } from '@/hooks/features/useFeatureAddressValidation';

import RequestOverrideDialog from './prompts/RequestOverrideDialog';
import { ReservationTripLegForm } from './ReservationTripLegForm';
import { ReservationMemberForm } from './ReservationMemberForm';
import TabPanel from '@/components/TabPanel';
import { useClientPortalPickupOffsetBlock } from './hooks/useClientPortalPickupOffsetBlock';
import { useDebouncedReservationActionSelectors } from './hooks/useDebouncedReservationActionSelectors';
import { useFieldActorSnapshot } from './hooks/useFieldActorSnapshot';
import { LevelOfCareContext } from './machines/levelOfCare.machine';
import { syncReservationFormMachinesFromValues } from './utils/syncReservationFormMachinesFromValues';
import {
  mergeAggregatedWithLiveForm,
  pickSecondaryAttributeFieldsFromForm,
} from './utils/getAggregatedFormValues';
import { ReservationFormSubmitProgress } from './ReservationFormSubmitProgress';
import { TripLegStatus } from '@/data/models/trip-leg-status.model';

const PREFIX = 'EditTripLegDialog';

function isSilentManualError(
  error: { type?: string; message?: string } | undefined,
): boolean {
  return error?.type === 'manual' && !error.message;
}

/**
 * `pickUpDueDateTime` is not a form control. The edit form holds the one leg
 * being edited at `trips[0]`, even when that leg's real sequence is higher.
 * Map those server errors onto the appointment and pickup date fields.
 */
export function dateFieldsForPickUpDueDateTimeErrors(
  errors: { fieldName?: string; tripLegSequence?: number }[],
  tripCount: number,
): FieldPath<ReservationFormInput>[] {
  if (tripCount <= 0) return [];
  const indexes = new Set<number>();
  for (const error of errors) {
    if (error.fieldName !== 'pickUpDueDateTime') continue;
    const sequence = error.tripLegSequence;
    if (
      typeof sequence === 'number' &&
      sequence >= 1 &&
      sequence <= tripCount
    ) {
      indexes.add(sequence - 1);
    } else if (tripCount === 1) {
      indexes.add(0);
    }
  }
  const fields: FieldPath<ReservationFormInput>[] = [];
  indexes.forEach((index) => {
    fields.push(
      `trips.${index}.appointmentDate` as FieldPath<ReservationFormInput>,
    );
    fields.push(`trips.${index}.pickupDate` as FieldPath<ReservationFormInput>);
  });
  return fields;
}

const classes = {
  title: `${PREFIX}-title`,
  content: `${PREFIX}-content`,
  pages: `${PREFIX}-pages`,
  loadingContainer: `${PREFIX}-loading-container`,
  errorContainer: `${PREFIX}-error-container`,
  errorActions: `${PREFIX}-error-actions`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  '& .MuiDialogTitle-root, & .MuiDialogActions-root, & .MuiLinearProgress-root':
    {
      flexShrink: 0,
    },
  '& .MuiDialogContent-root': {
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
  },
  [`& .${classes.title}`]: {
    lineHeight: '32px',
    paddingBottom: theme.spacing(3),
  },
  [`& .${classes.content}`]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: 0,
    paddingBottom: theme.spacing(3),
    [theme.breakpoints.up('md')]: {
      paddingLeft: theme.spacing(10),
      paddingRight: theme.spacing(10),
      paddingTop: 0,
      paddingBottom: theme.spacing(3),
    },
    [theme.breakpoints.up('lg')]: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      paddingTop: 0,
      paddingBottom: theme.spacing(3),
    },
  },
  [`& .${classes.pages}`]: {
    marginTop: theme.spacing(3),
  },
  [`& .${classes.loadingContainer}`]: {
    textAlign: 'center',
    minHeight: 200,
    minWidth: 444,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  [`& .${classes.errorContainer}`]: {
    width: 444,
  },
  [`& .${classes.errorActions}`]: {
    justifyContent: 'end',
    '& button': {
      flex: 'none',
      width: 144,
    },
  },
}));

function EditTripLegDialogContent({
  form,
  resetServerFieldErrors,
  stateMachineDebugEnabled,
  title,
  servicePlans,
  transportationAttributes,
  open,
  sequence,
  tripLeg,
  tripType,
  passenger,
  reservationReferenceID,
  onClose,
}: {
  form: ReturnType<
    typeof useForm<ReservationFormInput, unknown, ReservationFormValues>
  >;
  /** Clears the persisted server errors; call with every `form.reset`. */
  resetServerFieldErrors: () => void;
  stateMachineDebugEnabled: boolean;
  title: string;
  servicePlans: ServicePlan[];
  transportationAttributes: TransportationAttribute[];
  open: boolean;
  sequence: number;
  tripLeg?: TripLeg;
  tripType: TripLegType;
  passenger: Passenger;
  reservationReferenceID: string;
  onClose: () => void;
}) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const currentTripLeg = useAppSelector(reservationSlice.selectCurrentTripLeg);
  const savedTripLeg = useAppSelector(reservationSlice.selectSavedTripLeg);
  const isSavingTrip = useAppSelector(reservationSlice.selectSavingTripLeg);
  const saveSuccessMessage = useAppSelector(uiSlice.selectSuccessMessage);
  const [tabIndex, setTabIndex] = useState(0);
  const [isFormHydrated, setIsFormHydrated] = useState(false);
  const { isMemberPortal } = usePortalContext();
  const addressValidationEnabled =
    useFeatureAddressValidationForCurrentPortal();

  // Prefer the full getTripLeg payload (currentTripLeg). List/details props can
  // omit member fields; wait for the matching fetched leg before hydrating.
  const hydratedTripLeg = useMemo(() => {
    if (
      currentTripLeg?.tripLegReferenceID &&
      currentTripLeg.tripLegReferenceID === tripLeg?.tripLegReferenceID
    ) {
      return currentTripLeg;
    }
    return undefined;
  }, [currentTripLeg, tripLeg?.tripLegReferenceID]);

  const { mainSnapshot, fieldRefs, mainActor } = useReservationFormMachines();
  const levelOfCareSnap = useFieldActorSnapshot(fieldRefs.levelOfCare);
  const { isCareLevelMismatch } = levelOfCareSnap.context as LevelOfCareContext;

  const watchedTrips = form.watch('trips');
  const topLevelFormWatch = form.watch([
    'AllowOverride',
    'OverrideFirstName',
    'OverrideLastName',
    'OverrideReason',
    'OverrideServicePlan',
    'MemberHomePhone',
    'MemberMobilePhone',
    'AllowText',
    'genericErrors',
    'AlternateContact',
    'AlternateContactType',
    'AlternateContactFirstName',
    'AlternateContactLastName',
    'AlternateContactPhone',
    'AdditionalPassengers',
    'AdditionalPassengerCount',
    'PassengerNoteTypes',
    'DriverNoteTypes',
    'RequestedTransportationProviderID',
    'ShowOtherInputs',
    'AdditionalAttributes',
    'RequestedProvider',
  ]);
  const tripLegCount = watchedTrips?.length ?? 0;
  const hasCareLevelMismatch = isCareLevelMismatch
    .slice(0, tripLegCount)
    .some(Boolean);

  const mergedValues = useMemo(
    () => mergeAggregatedWithLiveForm(mainSnapshot.context, form.getValues()),
    [mainSnapshot.context, watchedTrips, topLevelFormWatch],
  );

  /**
   * Keep tripLegMergedValues in sync from the dialog shell so the Done button
   * stays accurate even when the trip-leg tab is unmounted (member tab active).
   */
  useLayoutEffect(() => {
    if (!isFormHydrated) {
      return;
    }
    const existing = mainActor.getSnapshot().context.tripLegMergedValues;
    if (existing !== undefined && isEqual(existing, mergedValues)) {
      return;
    }
    mainActor.send({
      type: 'TRIP_LEG_MERGED_VALUES',
      mergedValues,
    });
  }, [mainActor, mergedValues, isFormHydrated]);

  const { requestOverrideButtonDisabled, editTripLegDoneButtonDisabled } =
    useDebouncedReservationActionSelectors(mainSnapshot, undefined, {
      isFormHydrated,
      isMemberPortal,
      hasCareLevelMismatch,
    });
  // The pickup lead-time message is not part of the Zod schema that gates Done.
  const pickupOffsetBlocksDone = useClientPortalPickupOffsetBlock(
    form,
    fieldRefs.tripLegEstimatedPickupRefs,
  );
  const { isDirty } = form.formState;
  const pastPickupDateErrorFieldsRef = useRef<
    FieldPath<ReservationFormInput>[]
  >([]);
  // Validation result already on the machine when this dialog hydrates. Ignore
  // it so a previous session cannot mark the date fields before the next
  // /validate response.
  const ignoredValidationResultRef = useRef<ReservationValidations | null>(
    mainActor.getSnapshot().context.lastValidationResult ?? null,
  );

  const memberHydrationKey = hydratedTripLeg
    ? [
        hydratedTripLeg.tripLegPassengerNoteTypeBCs?.join(',') ?? '',
        hydratedTripLeg.tripLegDriverNoteTypeBCs?.join(',') ?? '',
        hydratedTripLeg.alternateContact?.phoneNumber ?? '',
        hydratedTripLeg.alternateContact?.firstName ?? '',
        String(hydratedTripLeg.companionCount ?? ''),
        String(hydratedTripLeg.requestedTransportationProviderID ?? ''),
        String(hydratedTripLeg.transportationAttributes?.length ?? 0),
        String(passenger?.passengerPhones?.length ?? 0),
      ].join('|')
    : '';

  const initKey = open
    ? `${hydratedTripLeg?.tripLegReferenceID ?? 'new'}:${tripType}:${passenger.passengerUUID}:${reservationReferenceID}:${transportationAttributes.length}:${memberHydrationKey}`
    : '';
  const lastSyncedInitKeyRef = useRef<string | null>(null);
  const mainActorRef = useRef(mainActor);
  const fieldRefsRef = useRef(fieldRefs);
  mainActorRef.current = mainActor;
  fieldRefsRef.current = fieldRefs;

  useLayoutEffect(() => {
    if (!open) {
      lastSyncedInitKeyRef.current = null;
      setIsFormHydrated(false);
      ignoredValidationResultRef.current =
        mainActorRef.current.getSnapshot().context.lastValidationResult ?? null;
      return;
    }
    if (!hydratedTripLeg) {
      setIsFormHydrated(false);
      return;
    }
    if (lastSyncedInitKeyRef.current === initKey) {
      setIsFormHydrated(true);
      return;
    }
    // Full leg can arrive after an earlier hydrate; don't wipe in-progress edits.
    if (lastSyncedInitKeyRef.current != null && form.formState.isDirty) {
      setIsFormHydrated(true);
      return;
    }
    lastSyncedInitKeyRef.current = initKey;

    const values = buildReservationFormValuesFromTripLeg(
      passenger,
      reservationReferenceID,
      hydratedTripLeg,
      tripType,
      transportationAttributes,
    );

    resetServerFieldErrors();
    form.reset(values, { keepErrors: false });
    ignoredValidationResultRef.current =
      mainActorRef.current.getSnapshot().context.lastValidationResult ?? null;
    syncReservationFormMachinesFromValues(
      mainActorRef.current,
      fieldRefsRef.current,
      values,
    );
    setIsFormHydrated(true);
    // Zod first, then /validate. The Create flow gets this update from the field-edit watch
    // but the Edit flow is hydrated from the existing leg so nothing would otherwise
    // send FIELD_EDITED. Wait for zod's trigger to complete so we don't clobber server errors.
    let cancelled = false;
    void form.trigger(undefined, { shouldFocus: false }).finally(() => {
      if (cancelled) return;
      sendReservationFieldEdited(form, mainActorRef.current, passenger);
    });
    return () => {
      cancelled = true;
    };
  }, [
    open,
    initKey,
    form,
    hydratedTripLeg,
    tripType,
    passenger,
    reservationReferenceID,
    transportationAttributes,
    resetServerFieldErrors,
  ]);

  // fieldName pickUpDueDateTime is not mapped onto a control, so it only shows
  // in the generic server-error list. Mark the appointment and pickup date
  // fields invalid without a second message under them.
  useEffect(() => {
    const result = mainSnapshot.context.lastValidationResult ?? null;
    const dateFields =
      open && result !== ignoredValidationResultRef.current
        ? dateFieldsForPickUpDueDateTimeErrors(
            result?.errors ?? [],
            tripLegCount,
          )
        : [];
    const next = new Set(dateFields);
    const toClear = pastPickupDateErrorFieldsRef.current.filter(
      (field) =>
        !next.has(field) &&
        isSilentManualError(form.getFieldState(field).error),
    );
    if (toClear.length > 0) {
      form.clearErrors(toClear);
    }
    for (const field of dateFields) {
      const error = form.getFieldState(field).error;
      if (error?.message || isSilentManualError(error)) continue;
      form.setError(field, { type: 'manual' });
    }
    pastPickupDateErrorFieldsRef.current = dateFields;
  }, [form, open, tripLegCount, mainSnapshot.context.lastValidationResult]);

  const handleTabChange = (_evt: React.ChangeEvent<{}>, newValue: number) => {
    setTabIndex(newValue);
  };

  const handleClose = () => {
    setTabIndex(0);
    clearTripLegScoreCache();
    dispatch(reservationSlice.reset());
    onClose();
  };

  useEffect(() => {
    if (open && saveSuccessMessage && savedTripLeg) {
      handleClose();
    }
  }, [open, saveSuccessMessage, savedTripLeg]);

  const handleOverrideOpen = () => {
    mainActor.send({ type: 'OVERRIDE_OPEN' });
  };

  const saveTrips = async (
    formValues: ReservationFormValues,
    saveAsDraft: boolean,
  ) => {
    // Mark saving before the pre-dispatch scoring so Done/Cancel (gated on
    // isSavingTrip) can't fire or close the dialog during the await.
    dispatch(reservationSlice.setSavingTripLeg(true));
    const gurneyData = getGurneyDataFromReservationFormSnapshot(
      mainActor.getSnapshot(),
      formValues,
    );

    if (tripLeg) {
      const fetchedOverrides =
        currentTripLeg?.tripLegReferenceID === tripLeg.tripLegReferenceID
          ? currentTripLeg?.overrides
          : tripLeg.overrides;
      const reservation =
        reservationUtils.getUpdateTripLegsPayloadFromReservationForm(
          passenger.clientBC,
          passenger.passengerUUID!,
          formValues,
          {
            reservationReferenceID,
            tripLeg,
            overrides: [...(fetchedOverrides ?? [])],
          },
          undefined,
          gurneyData,
        );
      // Same confidence patching as the create submit; the bundled-validation
      // score cache makes this a no-network merge for already-scored addresses.
      const tripLegsToSend = addressValidationEnabled
        ? await scoreAndMergeConfidenceIntoTripLegs(reservation.tripLegs, false)
        : reservation.tripLegs;
      dispatch(
        reservationSlice.updateTripLegsAsync(tripLegsToSend, saveAsDraft),
      );
      return;
    }

    const { reservation } =
      reservationUtils.getCreateReservationPayloadFromReservationForm(
        passenger.clientBC,
        passenger.passengerUUID!,
        formValues,
        reservationUtils.getPassengerPhone(passenger),
        gurneyData,
      );
    const newTripLeg = reservation.tripLegs[0];
    newTripLeg.tripLegSequence = sequence + 1;
    newTripLeg.metadata = {
      ...newTripLeg.metadata,
      originalTripLegSequence: sequence + 1,
    };
    newTripLeg.reservationReferenceID = reservationReferenceID;
    const passengerPhone = reservationUtils.getPassengerPhone(passenger);
    newTripLeg.pickUpPhone = newTripLeg.pickUpPhone ?? passengerPhone;
    newTripLeg.dropOffPhone = newTripLeg.dropOffPhone ?? passengerPhone;
    const [newTripLegToSend] = addressValidationEnabled
      ? await scoreAndMergeConfidenceIntoTripLegs([newTripLeg], false)
      : [newTripLeg];
    dispatch(
      reservationSlice.addTripLegAsync(
        reservationReferenceID,
        newTripLegToSend,
        saveAsDraft,
      ),
    );
  };

  const save = (_data: ReservationFormValues) => {
    const live = form.getValues();
    const formValues = mergeAggregatedWithLiveForm(
      mainActor.getSnapshot().context,
      {
        ...live,
        ...pickSecondaryAttributeFieldsFromForm(
          live as Record<string, unknown>,
        ),
      },
    );
    saveTrips(formValues, false);
  };

  const saveAsDraft = () => {
    const formValues = mergeAggregatedWithLiveForm(
      mainActor.getSnapshot().context,
      form.getValues(),
    );
    saveTrips(formValues, true);
  };

  return (
    <>
      <ReservationFormSubmitProgress />
      <DialogTitle className={classes.title}>{title}</DialogTitle>
      <DialogContent className={classes.content}>
        <Tabs
          indicatorColor="primary"
          textColor="primary"
          value={tabIndex}
          onChange={handleTabChange}>
          <Tab label={t('reservation.tripDetails')} data-testid="trip-tab" />
          <Tab
            label={t('reservation.memberDetails')}
            data-testid="member-tab"
          />
        </Tabs>
        <Box className={classes.pages}>
          {!isFormHydrated ? (
            <LinearProgress />
          ) : (
            <>
              {stateMachineDebugEnabled && <ReservationFormDebug form={form} />}
              <TabPanel value={tabIndex} index={0}>
                <ReservationTripLegForm form={form} isEditing={true} />
              </TabPanel>
              <TabPanel value={tabIndex} index={1}>
                <ReservationMemberForm
                  isEditing={true}
                  form={form}
                  transportationAttributes={transportationAttributes ?? []}
                />
              </TabPanel>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          variant="contained"
          disabled={
            editTripLegDoneButtonDisabled ||
            pickupOffsetBlocksDone ||
            isSavingTrip ||
            !isDirty
          }
          onClick={form.handleSubmit(save)}
          data-fs-element="EditTripSubmitButton">
          {t('common.done')}
        </Button>
        <Button
          color="secondary"
          variant="contained"
          disabled={mainSnapshot.matches('submitting') || isSavingTrip}
          onClick={() => handleClose()}>
          {t('common.cancel')}
        </Button>
        {!isMemberPortal && (
          <Button
            color="secondary"
            variant="contained"
            disabled={requestOverrideButtonDisabled}
            onClick={() => handleOverrideOpen()}>
            {t('reservation.requestOverride')}
          </Button>
        )}
        {tripLeg?.state === TripLegStatus.Draft && (
          <Button
            color="secondary"
            variant="contained"
            disabled={isSavingTrip}
            onClick={saveAsDraft}>
            {t('reservation.saveAsDraft')}
          </Button>
        )}
      </DialogActions>
    </>
  );
}

interface EditTripLegDialogProps {
  sequence: number;
  reservationReferenceID: string;
  tripLeg?: TripLeg;
  tripType: TripLegType;
  passenger: Passenger;
  servicePlans: ServicePlan[];
  open: boolean;
  onClose: () => void;
}

const EditTripLegDialog = ({
  sequence,
  reservationReferenceID,
  tripLeg,
  tripType,
  passenger,
  servicePlans,
  open,
  onClose,
}: EditTripLegDialogProps) => {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const { isPortal } = usePortalContext();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Check the runtimeConfig setting for state machine debugging
  const { __environment__ } = window as any;
  const stateMachineDebugEnabled = Boolean(
    __environment__?.REACT_APP_STATE_MACHINE_DEBUG,
  );

  const syncedTripLegs = useAppSelector(tripLegSlice.selectTripLegDetails);
  const loadingSyncedTripLegs = useAppSelector(
    tripLegSlice.selectLoadingTripLegDetails,
  );
  const transportationAttributes = useAppSelector(
    accountingSlice.selectTransportationAttributes,
  );

  const isAddMode = !tripLeg;
  const canEditTripLeg = useMemo(() => {
    if (!tripLeg?.tripLegReferenceID) return true;

    return reservationUtils.canPortalUserEditTripLeg(
      syncedTripLegs[tripLeg.tripLegReferenceID!],
      isPortal,
    );
  }, [syncedTripLegs, isPortal, tripLeg?.tripLegReferenceID]);

  // Hold the dialog spinner until getTripLegAsync lands so the form does not
  // mount into its own progress bar right after this spinner. Gated on open so
  // the content stays mounted while the dialog animates closed.
  const currentTripLeg = useAppSelector(reservationSlice.selectCurrentTripLeg);
  const waitingForTripLeg =
    open &&
    canEditTripLeg &&
    currentTripLeg?.tripLegReferenceID !== tripLeg?.tripLegReferenceID;
  const showLoadingContent =
    !isAddMode && (loadingSyncedTripLegs || waitingForTripLeg);

  const formDefaults = useMemo(() => {
    if (tripLeg) {
      return buildReservationFormValuesFromTripLeg(
        passenger,
        reservationReferenceID,
        tripLeg,
        tripType,
        transportationAttributes,
      );
    }
    return {
      ...buildEmptyReservationFormValues(passenger, transportationAttributes),
      tripType,
    };
  }, [
    tripLeg,
    tripType,
    passenger?.passengerUUID,
    reservationReferenceID,
    transportationAttributes,
  ]);

  const {
    resolver,
    serverFieldErrorsRef,
    bindControl,
    resetServerFieldErrors,
  } = useReservationFormResolver();
  const form = useForm<ReservationFormInput, unknown, ReservationFormValues>({
    resolver,
    defaultValues: formDefaults,
    mode: 'onBlur',
  });
  bindControl(form.control);
  const { isDirty } = form.formState;
  const pickUpTime = form.watch('trips.0.pickupDateTime');
  const appointmentDate = form.watch('trips.0.appointmentDate');

  // The member tab's phone fields validate via the resolver on blur/submit;
  // tab state lives in the dialog content, so unlike CreateReservationDialog
  // (wizard-step-aware) they are always excluded here.
  const applyTripLegValidationRef = useTripLegValidationFormApplier(
    form,
    () => ({ fieldsToExclude: MEMBER_FORM_PHONE_FIELDS }),
    // Optional-chained: ReservationList mounts this dialog (closed) before the
    // passenger record loads, so the prop can be null despite its type.
    passenger?.clientBC,
    passenger?.servicePlans?.[0]?.servicePlanUUID,
    serverFieldErrorsRef,
  );

  const tripLegServiceDate =
    (pickUpTime instanceof DateTime && pickUpTime.isValid
      ? pickUpTime.toISODate()
      : null) ??
    (appointmentDate instanceof DateTime && appointmentDate.isValid
      ? appointmentDate.toISODate()
      : null) ??
    (typeof appointmentDate === 'string' && appointmentDate.trim()
      ? appointmentDate
      : null) ??
    undefined;
  const overridableServicePlans = servicePlans.filter(
    ({ effectiveStartDate, effectiveEndDate }) =>
      tripLegServiceDate != null &&
      effectiveStartDate <= tripLegServiceDate &&
      (!effectiveEndDate || tripLegServiceDate <= effectiveEndDate),
  );

  useEffect(() => {
    // Fetch all Create Reservation dialog data that isn't passed via props
    dispatch(lookupSlice.fetchTransportationAttributeMapAsync());
    dispatch(lookupSlice.fetchLookupItemsAsync('timeZone'));
    dispatch(lookupSlice.fetchLookupItemsAsync('tripLegPassengerNoteType'));
    dispatch(lookupSlice.fetchLookupItemsAsync('tripLegDriverNoteType'));
  }, []);

  useEffect(() => {
    if (!open || !passenger?.passengerUUID) {
      return;
    }
    // Care-level mismatch messaging needs preferences in Redux; parent pages
    // that open this dialog (trip details / reservation list) may not fetch them.
    dispatch(
      fetchPassengerPreferencesAsync(
        passenger.passengerUUID,
        passenger.clientBC,
      ),
    );
  }, [open, passenger?.passengerUUID, passenger?.clientBC, dispatch]);

  useEffect(() => {
    if (
      !open ||
      !tripLeg?.tripLegReferenceID ||
      !passenger?.passengerUUID ||
      !reservationReferenceID
    ) {
      return;
    }
    // List/details props can omit member fields; always load the full leg for
    // ReservationMemberForm hydration.
    dispatch(
      reservationSlice.getTripLegAsync(
        passenger.passengerUUID,
        reservationReferenceID,
        tripLeg.tripLegReferenceID,
      ),
    );
  }, [
    open,
    tripLeg?.tripLegReferenceID,
    passenger?.passengerUUID,
    reservationReferenceID,
    dispatch,
  ]);

  useLayoutEffect(() => {
    if (!open || !isPortal || !tripLeg?.tripLegReferenceID) {
      return;
    }
    dispatch(tripLegSlice.getTripLegDetailsAsync(tripLeg.tripLegReferenceID));
  }, [open, isPortal, tripLeg?.tripLegReferenceID, dispatch]);

  const handleClose = () => {
    clearTripLegScoreCache();
    dispatch(reservationSlice.reset());
    // reset() keeps currentTripLeg. Left in place, the next open hydrates from
    // this pre-save leg before getTripLegAsync returns, and initKey does not
    // cover pickup/dropoff phones, so the fresh leg never re-hydrates the form.
    dispatch(reservationSlice.setCurrentTripLeg(null));
    onClose();
  };

  const renderErrorContent = () => {
    return (
      <>
        <DialogTitle>{t('tripLegErrors.dialogHeader')}</DialogTitle>
        <DialogContent>
          <Box className={classes.errorContainer}>
            <p>{t('tripLegErrors.dialogTitle')}</p>
            <ul>
              <li>{t('tripLegErrors.time')}</li>
              <li>{t('tripLegErrors.provider')}</li>
              <li>{t('tripLegErrors.driver')}</li>
              <li>{t('tripLegErrors.offered')}</li>
            </ul>
            <p>
              {t('tripLegErrors.dialogFooter')} <b>{t('footer.phoneNumber')}</b>
            </p>
          </Box>
        </DialogContent>
        <DialogActions className={classes.errorActions}>
          <Button variant="contained" onClick={handleClose}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </>
    );
  };

  const renderLoadingContent = () => {
    return (
      <DialogContent>
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      </DialogContent>
    );
  };

  return (
    <ReservationFormMachinesProvider
      clientBC={passenger?.clientBC ?? ''}
      passengerUUID={passenger?.passengerUUID ?? ''}
      passengerPhone={
        passenger ? reservationUtils.getPassengerPhone(passenger) : undefined
      }
      standingOrderUUID={tripLeg?.reservation?.standingOrderUUID || undefined}
      applyTripLegValidationResultsToForm={() =>
        applyTripLegValidationRef.current
      }
      getPickupTimeZoneForTrip={(tripIndex) =>
        form.getValues('trips')[tripIndex]?.pickupTimeZone || undefined
      }
      getAddressStructsForTrip={(tripIndex) => {
        const trip = form.getValues('trips')[tripIndex];
        return {
          pickup: trip?.pickupAddressStruct,
          dropoff: trip?.dropoffAddressStruct,
        };
      }}>
      <StyledDialog
        open={open}
        onClose={handleClose}
        fullWidth={isAddMode || canEditTripLeg}
        maxWidth="md">
        {showLoadingContent && renderLoadingContent()}
        {(isAddMode || (!showLoadingContent && canEditTripLeg)) && (
          <EditTripLegDialogContent
            form={form}
            resetServerFieldErrors={resetServerFieldErrors}
            stateMachineDebugEnabled={stateMachineDebugEnabled}
            title={t('reservation.editTripLeg')}
            servicePlans={servicePlans}
            transportationAttributes={transportationAttributes ?? []}
            open={open}
            sequence={sequence}
            tripLeg={tripLeg}
            tripType={tripType}
            passenger={passenger}
            reservationReferenceID={reservationReferenceID}
            onClose={handleClose}
          />
        )}
        {!isAddMode &&
          !loadingSyncedTripLegs &&
          !canEditTripLeg &&
          renderErrorContent()}
      </StyledDialog>
      <RequestOverrideDialog
        servicePlans={overridableServicePlans}
        reservationForm={form}
      />
    </ReservationFormMachinesProvider>
  );
};

export { EditTripLegDialog };
